import React from 'react';
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { useTranslation } from 'react-i18next';

import type { Schema } from '../../../api';
import { fmtLocalDateTime } from '../../../utils/datetime';

type PunchDeviceBinding = Schema<'PunchDeviceBindingResponse'>;

interface Props {
  state?: PunchDeviceBinding;
  loading?: boolean;
  error?: boolean;
  onReset: () => void;
}

const PunchDeviceStatus: React.FC<Props> = ({
  state,
  loading = false,
  error = false,
  onReset,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return <CircularProgress size={18} aria-label={t('org:faceTemplates.punchDevice.loading')} />;
  }

  if (error || !state) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="error"
        label={t('org:faceTemplates.punchDevice.unavailable')}
        sx={{ height: 22, borderRadius: 1, fontSize: '0.7rem' }}
      />
    );
  }

  const status = !state.single_device_enforced
    ? 'notRequired'
    : state.status === 'pending'
      ? 'pending'
      : state.bound
        ? 'approved'
        : 'unbound';
  const color = status === 'approved'
    ? 'success'
    : status === 'pending'
      ? 'warning'
      : 'default';
  const canReset = state.single_device_enforced
    && (state.bound || (state.trusted_device_count ?? 0) > 0);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <BoxContent
        status={status}
        color={color}
        lastPunchAt={state.last_punch_at}
      />
      {canReset && (
        <Tooltip title={t('org:faceTemplates.punchDevice.resetTooltip')}>
          <IconButton
            size="small"
            color="warning"
            aria-label={t('org:faceTemplates.punchDevice.resetTooltip')}
            onClick={onReset}
          >
            <RestartAltOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

interface BoxContentProps {
  status: 'notRequired' | 'pending' | 'approved' | 'unbound';
  color: 'default' | 'success' | 'warning';
  lastPunchAt?: string | null;
}

const BoxContent: React.FC<BoxContentProps> = ({ status, color, lastPunchAt }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Chip
        size="small"
        variant="outlined"
        color={color}
        icon={<PhoneIphoneOutlinedIcon />}
        label={t(`org:faceTemplates.punchDevice.${status}`)}
        sx={{
          alignSelf: 'flex-start',
          height: 22,
          borderRadius: 1,
          fontSize: '0.7rem',
          '& .MuiChip-icon': { fontSize: 15 },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {lastPunchAt
          ? t('org:faceTemplates.punchDevice.lastPunch', {
              date: fmtLocalDateTime(lastPunchAt),
            })
          : t('org:faceTemplates.punchDevice.noPunch')}
      </Typography>
    </Stack>
  );
};

export default PunchDeviceStatus;
